// src/components/appSettings/Contact/ContactInfoDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiEdit2,
  FiTrash2,
  FiMapPin,
  FiPhone,
  FiMail,
  FiGlobe,
  FiClock,
  FiMessageCircle,
  FiCheck,
  FiX,
  FiArrowLeft,
  FiExternalLink,
  FiAward,
  FiShield,
  FiPackage,
} from "react-icons/fi";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
  FaWhatsapp,
} from "react-icons/fa";
import {
  fetchContactInfoByIdThunk,
  deleteContactInfoThunk,
  updateContactInfoThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const ContactInfoDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedContactInfo, loading } = useSelector(
    (state) => state.appSettings
  );

  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (id) {
      dispatch(fetchContactInfoByIdThunk(id));
    }
  }, [id, dispatch]);

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this contact information? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(deleteContactInfoThunk(id)).unwrap();
        toast.success("Contact information deleted successfully");
        navigate("/app-settings/contact");
      } catch (error) {
        toast.error(error.message || "Failed to delete contact information");
      }
    }
  };

  const handleToggleStatus = async () => {
    try {
      await dispatch(
        updateContactInfoThunk({
          id,
          contactData: { isActive: !selectedContactInfo.isActive },
        })
      ).unwrap();
      toast.success(
        `Contact ${
          selectedContactInfo.isActive ? "deactivated" : "activated"
        } successfully`
      );
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const isStoreOpen = () => {
    if (!selectedContactInfo?.businessHours?.regular) return null;

    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentTime = now.getHours() * 60 + now.getMinutes();

    // Map day index to day code
    const dayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const todayCode = dayMap[dayOfWeek];

    const todayHours = selectedContactInfo.businessHours.regular.find(
      (h) => h.day === todayCode
    );

    if (!todayHours || !todayHours.isOpen) return false;

    const [openHour, openMin] = todayHours.openTime.split(":").map(Number);
    const [closeHour, closeMin] = todayHours.closeTime.split(":").map(Number);

    const openMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;

    return currentTime >= openMinutes && currentTime <= closeMinutes;
  };

  if (loading || !selectedContactInfo) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const storeStatus = isStoreOpen();

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app-settings/contact")}
              >
                <FiArrowLeft className="mr-2" />
                Back to Contacts
              </Button>
              {selectedContactInfo.isMainBranch && (
                <span className="inline-flex px-3 py-1 text-sm rounded-full bg-primary text-white">
                  Main Branch
                </span>
              )}
              {storeStatus !== null && (
                <span
                  className={`inline-flex items-center px-3 py-1 text-sm rounded-full ${
                    storeStatus
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {storeStatus ? (
                    <>
                      <FiCheck className="mr-1" />
                      Open Now
                    </>
                  ) : (
                    <>
                      <FiX className="mr-1" />
                      Closed
                    </>
                  )}
                </span>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900">
              {selectedContactInfo.storeName}
            </h1>
            <p className="text-gray-600 mt-1">
              Branch Code: {selectedContactInfo.branchCode}
            </p>
            {selectedContactInfo.storeDescription && (
              <p className="text-gray-600 mt-2">
                {selectedContactInfo.storeDescription}
              </p>
            )}
            {selectedContactInfo.establishedYear && (
              <p className="text-sm text-gray-500 mt-1">
                Established: {selectedContactInfo.establishedYear}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleToggleStatus}
              className={
                selectedContactInfo.isActive ? "text-red-600" : "text-green-600"
              }
            >
              {selectedContactInfo.isActive ? "Deactivate" : "Activate"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/app-settings/contact/edit/${id}`)}
            >
              <FiEdit2 className="mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:bg-red-50"
            >
              <FiTrash2 className="mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <div className="flex space-x-8 px-6">
            {["overview", "hours", "services", "social"].map((tab) => (
              <button
                key={tab}
                className={`py-3 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === "overview" && (
            <OverviewTab contact={selectedContactInfo} />
          )}
          {activeTab === "hours" && (
            <BusinessHoursTab contact={selectedContactInfo} />
          )}
          {activeTab === "services" && (
            <ServicesTab contact={selectedContactInfo} />
          )}
          {activeTab === "social" && (
            <SocialMediaTab contact={selectedContactInfo} />
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ contact }) => (
  <div className="space-y-6">
    {/* Contact Information */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <InfoSection title="Contact Details" icon={FiPhone}>
        {contact.contact?.primaryPhone && (
          <ContactRow
            icon={FiPhone}
            label="Primary Phone"
            value={contact.contact.primaryPhone}
            href={`tel:${contact.contact.primaryPhone}`}
          />
        )}
        {contact.contact?.secondaryPhone && (
          <ContactRow
            icon={FiPhone}
            label="Secondary Phone"
            value={contact.contact.secondaryPhone}
            href={`tel:${contact.contact.secondaryPhone}`}
          />
        )}
        {contact.contact?.email && (
          <ContactRow
            icon={FiMail}
            label="Email"
            value={contact.contact.email}
            href={`mailto:${contact.contact.email}`}
          />
        )}
        {contact.contact?.website && (
          <ContactRow
            icon={FiGlobe}
            label="Website"
            value={contact.contact.website}
            href={contact.contact.website}
            external
          />
        )}
        {contact.contact?.whatsapp && (
          <ContactRow
            icon={FaWhatsapp}
            label="WhatsApp"
            value={contact.contact.whatsapp}
            href={`https://wa.me/${contact.contact.whatsapp.replace(
              /\D/g,
              ""
            )}`}
            external
          />
        )}
      </InfoSection>

      <InfoSection title="Address" icon={FiMapPin}>
        {contact.address && (
          <div className="space-y-2">
            <p className="text-sm text-gray-900">
              {contact.address.line1}
              {contact.address.line2 && `, ${contact.address.line2}`}
            </p>
            {contact.address.landmark && (
              <p className="text-sm text-gray-600">
                Landmark: {contact.address.landmark}
              </p>
            )}
            <p className="text-sm text-gray-900">
              {contact.address.city}, {contact.address.state}{" "}
              {contact.address.pincode}
            </p>
            <p className="text-sm text-gray-900">{contact.address.country}</p>
            {contact.address.googleMapsUrl && (
              <a
                href={contact.address.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-primary hover:underline mt-2"
              >
                <FiMapPin className="mr-1" />
                View on Google Maps
                <FiExternalLink className="ml-1" size={12} />
              </a>
            )}
          </div>
        )}
      </InfoSection>
    </div>

    {/* Map */}
    {contact.address?.coordinates?.latitude &&
      contact.address?.coordinates?.longitude && (
        <InfoSection title="Location Map" icon={FiMapPin}>
          <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center">
            <p className="text-gray-500">
              Map integration placeholder
              <br />
              Lat: {contact.address.coordinates.latitude}, Lng:{" "}
              {contact.address.coordinates.longitude}
            </p>
          </div>
        </InfoSection>
      )}

    {/* Support Channels */}
    {contact.support && (
      <InfoSection title="Customer Support" icon={FiMessageCircle}>
        <div className="space-y-4">
          {/* Customer Care */}
          {(contact.support.customerCare?.phone ||
            contact.support.customerCare?.email ||
            contact.support.customerCare?.timings) && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Customer Care
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {contact.support.customerCare.phone && (
                  <InfoRow
                    label="Phone"
                    value={contact.support.customerCare.phone}
                  />
                )}
                {contact.support.customerCare.email && (
                  <InfoRow
                    label="Email"
                    value={contact.support.customerCare.email}
                  />
                )}
                {contact.support.customerCare.timings && (
                  <InfoRow
                    label="Timings"
                    value={contact.support.customerCare.timings}
                  />
                )}
              </div>
            </div>
          )}

          {/* WhatsApp Support */}
          {contact.support.whatsappSupport?.enabled && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                WhatsApp Support
              </h4>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  <FaWhatsapp className="mr-1" />
                  {contact.support.whatsappSupport.businessAccount
                    ? "Business Account"
                    : "Available"}
                </span>
                {contact.support.whatsappSupport.number && (
                  <a
                    href={`https://wa.me/${contact.support.whatsappSupport.number.replace(
                      /\D/g,
                      ""
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    {contact.support.whatsappSupport.number}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Chat Support */}
          {contact.support.chatSupport?.enabled && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Live Chat Support
              </h4>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  <FiCheck className="mr-1" />
                  Available
                </span>
                {contact.support.chatSupport.platform && (
                  <span className="text-sm text-gray-600">
                    Platform: {contact.support.chatSupport.platform}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </InfoSection>
    )}

    {/* Store Images */}
    {(contact.images?.storeFront ||
      contact.images?.logo ||
      contact.images?.interior?.length > 0) && (
      <InfoSection title="Store Images" icon={FiPackage}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {contact.images.storeFront && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Store Front</p>
              <img
                src={contact.images.storeFront}
                alt="Store Front"
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}
          {contact.images.logo && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Logo</p>
              <img
                src={contact.images.logo}
                alt="Logo"
                className="w-full h-48 object-contain rounded-lg bg-gray-100"
              />
            </div>
          )}
          {contact.images.interior?.map((img, index) => (
            <div key={index}>
              <p className="text-xs text-gray-500 mb-2">Interior {index + 1}</p>
              <img
                src={img}
                alt={`Interior ${index + 1}`}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          ))}
        </div>
      </InfoSection>
    )}

    {/* Certifications */}
    {contact.certifications?.length > 0 && (
      <InfoSection title="Certifications" icon={FiAward}>
        <div className="space-y-3">
          {contact.certifications.map((cert, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg"
            >
              <FiShield className="text-blue-600 mt-1" size={20} />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{cert.name}</h4>
                {cert.authority && (
                  <p className="text-sm text-gray-600">
                    Issued by: {cert.authority}
                  </p>
                )}
                {cert.number && (
                  <p className="text-xs text-gray-500">
                    Certificate #: {cert.number}
                  </p>
                )}
                {cert.validUntil && (
                  <p className="text-xs text-gray-500">
                    Valid until:{" "}
                    {new Date(cert.validUntil).toLocaleDateString()}
                  </p>
                )}
              </div>
              {cert.image && (
                <img
                  src={cert.image}
                  alt={cert.name}
                  className="w-16 h-16 object-cover rounded"
                />
              )}
            </div>
          ))}
        </div>
      </InfoSection>
    )}
  </div>
);

// Business Hours Tab
const BusinessHoursTab = ({ contact }) => {
  const dayLabels = {
    SUN: "Sunday",
    MON: "Monday",
    TUE: "Tuesday",
    WED: "Wednesday",
    THU: "Thursday",
    FRI: "Friday",
    SAT: "Saturday",
  };

  return (
    <div className="space-y-6">
      <InfoSection title="Regular Business Hours" icon={FiClock}>
        <div className="space-y-2">
          {contact.businessHours?.regular?.map((hour, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <span className="font-medium text-gray-900 w-32">
                {dayLabels[hour.day]}
              </span>
              {!hour.isOpen ? (
                <span className="text-red-600 font-medium">Closed</span>
              ) : (
                <div className="text-right">
                  <span className="text-gray-900">
                    {hour.openTime} - {hour.closeTime}
                  </span>
                  {hour.breakTime?.start && hour.breakTime?.end && (
                    <p className="text-xs text-gray-500">
                      Break: {hour.breakTime.start} - {hour.breakTime.end}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </InfoSection>

      {contact.businessHours?.holidays?.length > 0 && (
        <InfoSection title="Holidays" icon={FiClock}>
          <div className="space-y-2">
            {contact.businessHours.holidays.map((holiday, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
              >
                <span className="text-gray-900">{holiday.name}</span>
                <span className="text-gray-600">
                  {new Date(holiday.date).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </InfoSection>
      )}
    </div>
  );
};

// Services Tab
const ServicesTab = ({ contact }) => {
  const servicesList = [
    { key: "homeDelivery", label: "Home Delivery", icon: FiPackage },
    { key: "videoShopping", label: "Video Shopping", icon: FiMessageCircle },
    {
      key: "appointmentBooking",
      label: "Appointment Booking",
      icon: FiClock,
    },
    { key: "customDesign", label: "Custom Design", icon: FiAward },
    { key: "repair", label: "Repair Services", icon: FiShield },
    { key: "exchange", label: "Exchange", icon: FiCheck },
  ];

  const availableServices = servicesList.filter(
    (service) => contact.services?.[service.key]
  );

  return (
    <div className="space-y-6">
      <InfoSection title="Available Services">
        {availableServices.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableServices.map((service) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.key}
                  className="flex items-center gap-3 p-3 bg-green-50 rounded-lg"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Icon className="text-green-600" />
                  </div>
                  <span className="text-gray-900 font-medium">
                    {service.label}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            No services configured
          </p>
        )}
      </InfoSection>
    </div>
  );
};

// Social Media Tab
const SocialMediaTab = ({ contact }) => {
  const socialPlatforms = [
    {
      key: "facebook",
      label: "Facebook",
      icon: FaFacebook,
      color: "text-blue-600",
    },
    {
      key: "instagram",
      label: "Instagram",
      icon: FaInstagram,
      color: "text-pink-600",
    },
    {
      key: "twitter",
      label: "Twitter",
      icon: FaTwitter,
      color: "text-blue-400",
    },
    {
      key: "youtube",
      label: "YouTube",
      icon: FaYoutube,
      color: "text-red-600",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: FaLinkedin,
      color: "text-blue-700",
    },
  ];

  const availablePlatforms = socialPlatforms.filter(
    (platform) => contact.socialMedia?.[platform.key]
  );

  return (
    <div className="space-y-6">
      <InfoSection title="Social Media Profiles">
        {availablePlatforms.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availablePlatforms.map((platform) => {
              const Icon = platform.icon;
              return (
                <a
                  key={platform.key}
                  href={contact.socialMedia[platform.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Icon className={`text-2xl ${platform.color}`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {platform.label}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {contact.socialMedia[platform.key]}
                    </p>
                  </div>
                  <FiExternalLink className="text-gray-400" />
                </a>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            No social media profiles configured
          </p>
        )}
      </InfoSection>
    </div>
  );
};

// Helper Components
const InfoSection = ({ title, icon: Icon, children }) => (
  <div className="bg-white border rounded-lg p-4">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="text-gray-600" />}
      <h3 className="font-medium text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-gray-500">{label}:</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

const ContactRow = ({ icon: Icon, label, value, href, external }) => (
  <div className="flex items-start justify-between py-2">
    <div className="flex items-center gap-2">
      <Icon className="text-gray-400 mt-0.5" size={16} />
      <span className="text-sm text-gray-500">{label}:</span>
    </div>
    {href ? (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
      >
        {value}
        {external && <FiExternalLink size={12} />}
      </a>
    ) : (
      <span className="text-sm font-medium text-gray-900">{value}</span>
    )}
  </div>
);

export default ContactInfoDetails;
